// (c) chaehongee. Confidential and proprietary.

import { useCallback, useRef } from 'react'
import { updateNodeImage } from '../../../../services/node'
import { mergeSessionEntities } from '../../../../store/slices/sessionSlice'
import { updateItemSrc as updateCanvasItemSrc } from '../../../../store/slices/canvasSlice'
import { exportCroppedImage } from '../../services/imageExport'
import type { Rect } from '../../types'

export interface ResizeCommitInfo {
    id: string
    rect: Rect
    src?: string
}

interface UseImageResizeCommitParams {
    sessionId: string | null
    canvasItems: Array<{ id: string; src: string; mediaType?: string }>
    imageSizesBySrc: Record<string, { w: number; h: number }>
    initialWorldRectsById: Record<string, Rect>
    dispatch: any
}

/**
 * Manages image resize commits - crops the image and uploads the result.
 * 
 * When a user resizes an image on the canvas (without frame editing),
 * this hook handles:
 * - Exporting the cropped region
 * - Uploading the new image to the server
 * - Updating Redux state with the new image URL
 * - Tracking initial rects for future resize operations
 */
export function useImageResizeCommit(params: UseImageResizeCommitParams) {
    const { sessionId, canvasItems, imageSizesBySrc, initialWorldRectsById, dispatch } = params

    // Track pending uploads to prevent duplicate commits
    const pendingUploadsRef = useRef<Set<string>>(new Set())

    const handleCommit = useCallback(
        async ({ id, rect, src }: ResizeCommitInfo) => {
            // Find source URL
            const sourceUrl = src ?? canvasItems.find((it) => it.id === id)?.src ?? null
            if (!sourceUrl) return
            const mediaType = canvasItems.find((it) => it.id === id)?.mediaType ?? 'image'
            if (mediaType === 'video') return

            // Get natural image size
            const naturalSize = imageSizesBySrc[sourceUrl]
            if (!naturalSize) return

            // Get base rect (initial rect or current rect)
            const baseRect = initialWorldRectsById[id] || rect

            // Prevent duplicate uploads
            if (pendingUploadsRef.current.has(id)) return
            pendingUploadsRef.current.add(id)

            try {
                // Export cropped image
                const result = await exportCroppedImage({
                    sourceUrl,
                    imageRect: rect,
                    frameRect: rect,
                    imageNaturalSize: naturalSize,
                    initialImageRect: baseRect,
                })

                if (!result) return

                // Create file and upload
                const file = new File([result.blob], `canvas-resize-${id}.png`, {
                    type: 'image/png',
                })
                const updatedNode = await updateNodeImage(id, file)

                // Update Redux state
                if (sessionId) dispatch(mergeSessionEntities({ sessionId, nodes: [updatedNode] }))
                if (updatedNode?.image_path) {
                    if (sessionId) {
                        dispatch(updateCanvasItemSrc({
                            sessionId,
                            id,
                            src: updatedNode.image_path
                        }))
                    }
                }

                // Note: Initial rects are managed by useCanvasItems
            } catch (err) {
                console.warn('[Canvas] Failed to update resized node', err)
            } finally {
                pendingUploadsRef.current.delete(id)
            }
        },
        [sessionId, canvasItems, imageSizesBySrc, initialWorldRectsById, dispatch]
    )

    return handleCommit
}

